/**
 * Lightweight in-memory request helpers for public AutoMapa pages (M17).
 * Not for admin/private data — short TTL only.
 */

import { buildPublicCacheKey } from "./publicCacheKey.js";

export { buildPublicCacheKey };

const inFlightRequests = new Map();
const memoryCache = new Map();

/**
 * Coalesce identical concurrent fetches into one promise.
 * @template T
 * @param {string} key
 * @param {() => Promise<T>} fetchFn
 * @returns {Promise<T>}
 */
export async function dedupeInFlightRequest(key, fetchFn) {
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  const promise = Promise.resolve()
    .then(fetchFn)
    .finally(() => {
      inFlightRequests.delete(key);
    });

  inFlightRequests.set(key, promise);
  return promise;
}

/**
 * @template T
 * @param {string} key
 * @param {number} ttlMs
 * @returns {T|null}
 */
export function readMemoryCache(key, ttlMs) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > ttlMs) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

/** @param {string} key @param {unknown} value */
export function writeMemoryCache(key, value) {
  memoryCache.set(key, { value, ts: Date.now() });
}

export function clearPublicRequestCache() {
  inFlightRequests.clear();
  memoryCache.clear();
}

/**
 * Incrementing guard for ignoring stale async responses after rapid filter changes.
 */
export function createStaleRequestGuard() {
  let latestId = 0;

  return {
    nextRequestId() {
      latestId += 1;
      return latestId;
    },
    isStale(requestId) {
      return requestId !== latestId;
    },
  };
}
